import { Role } from 'auth';
import classNames from 'classnames';
import { FormikProvider, useFormik } from 'formik';
import { pick } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import { SignUpPagesWrapper } from '@/modules/auth/components/SignUpPagesWrapper.tsx';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { useCurrentUserQuery } from '@/modules/auth/queries/user.tsx';
import { APPLY_REGISTER_ID } from '@/modules/common/constants/search-params.ts';
import { queryClient } from '@/modules/common/query.ts';
import { onlyNumbers } from '@/modules/common/utils/only-numbers.ts';
import { AUS_ABN, AUS_ACN } from '@/modules/common/utils/regex.ts';
import {
  USER_ENTITIES_QUERY_KEY,
  useCreateEntityMutation,
  useEntityItemUncachedQuery,
  useGetBronIdLookUpByAPNQuery,
  useUpdateEntityMutation,
} from '@/modules/entity/queries/entity.tsx';
import { entityPaths } from '@/modules/entity/routes.tsx';
import { CountryNameCodeEnum } from '@/modules/entity/static/countries.ts';
import {
  detectTrustType,
  trustTypeAUSRegulatedOptions,
} from '@/modules/entity/static/trust-types.ts';
import { EntityItemFullResponse } from '@/modules/entity/types/EntityItemResponse.ts';
import { isValidABN, validateABNorACN } from '@/modules/entity/utils/abn-acn.ts';
import { Button, Select, SelectButtons, TextInputField, getSelectOptionByEnum } from 'ui';
import { CountryCodeEnum, CreateEntityRequest, EntityType } from 'api';

export enum UseAbnOrAcn {
  'Enter details manually' = 'no',
  'Use ABN / ACN' = 'yes',
}

interface AbnLookupAndCountryFormik {
  abn_acn?: string;
  country?: CountryNameCodeEnum | CountryCodeEnum | string;
  use_abn_acn?: UseAbnOrAcn;
}

const validationSchema = yup.object({
  abn_acn: yup
    .string()
    .test(
      'abn-or-acn',
      'Incorrect ABN or ACN, example of ABN: 12345678901, example of ACN: 123456789',
      value => {
        if (!value) return true; // Allow nullable (optional) values
        return AUS_ABN.test(value) || AUS_ACN.test(value); // Validate against either ABN or ACN
      },
    )
    .test('is-valid-abn-or-acn', 'Please enter a valid ABN or ACN', value => {
      if (!value) return true; // Allow nullable (optional) values
      const type = validateABNorACN(value);
      if (type === 'ACN') return true; // ACN just needs 9 digits
      if (type === 'ABN') return isValidABN(value);
      return false;
    })
    .nullable()
    .when('use_abn_acn', ([use_abn_acn], schema) => {
      if (use_abn_acn === 'yes') {
        return schema.required('This field is required');
      }
      return schema;
    }),
  use_abn_acn: yup.mixed<UseAbnOrAcn>().oneOf(Object.values(UseAbnOrAcn)),
  country: yup.string().trim().required('This field is required'),
});

const ABNLookupAndCountryPage = () => {
  const { data: currentUser } = useCurrentUserQuery();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const apply_register_id = params.get(APPLY_REGISTER_ID);
  const location = useLocation();
  const state = location.state;
  const { data } = useEntityItemUncachedQuery(
    state?.entityId ? +state.entityId : -1,
    !state?.entityId || state.entityId === 0 || isNaN(+state.entityId),
  );

  const generateInitialValues = (data?: EntityItemFullResponse): AbnLookupAndCountryFormik => {
    const current = pick(data?.entity, ['country']);

    return {
      ...current,
      country: current?.country ?? 'AUS',
      use_abn_acn:
        (current?.country ?? 'AUS') === 'AUS'
          ? UseAbnOrAcn['Use ABN / ACN']
          : UseAbnOrAcn['Enter details manually'],
    };
  };

  const formik = useFormik<AbnLookupAndCountryFormik>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema,
    initialValues: generateInitialValues(data),
    enableReinitialize: true,
    onSubmit: async (values, { setFieldError }) => {
      if (values.abn_acn) {
        try {
          await mutateAsyncLookupByABN({ abn: values.abn_acn });
          return;
        } catch (e) {
          setFieldError('abn_acn', 'Not found info by this ABN / ACN');
          return;
        }
      }

      // case when registered through transfer
      if (data?.entity?.entity_id && !data?.entity?.type) {
        navigate(
          {
            pathname: entityPaths.static.createEntity,
            search: params.toString(),
          },
          { state: { entityId: state?.entityId, country: values?.country } },
        );
        return;
      }

      //IF NOT abn_acn
      if (data?.entity?.entity_id) {
        navigate(
          {
            pathname: entityPaths.static.updateEntity + `/${data.entity.id}`,
            search: params.toString(),
          },
          { state: { entityId: state?.entityId, country: values?.country } },
        );
      } else {
        navigate(
          {
            pathname: entityPaths.static.createEntity,
            search: params.toString(),
          },
          { state: { entityId: state?.entityId, country: values?.country } },
        );
      }
    },
  });

  const { mutateAsync: mutateAsyncLookupByABN, isLoading: isLoadingLookupByABN } =
    useGetBronIdLookUpByAPNQuery({
      onSuccess: async data => {
        let type = data?.data?.entity_type_vestra as EntityType | undefined;

        const entityTypeAbn = data?.data?.entity_type_abn;
        const trustType = detectTrustType(entityTypeAbn);
        const isRegulated = trustTypeAUSRegulatedOptions
          .map(item => item.value)
          .includes(String(trustType));
        // const phone_number = data?.data?.entityNumber;
        const state = data?.data?.address_state;
        const postcode = data?.data?.address_postcode;
        const address = data?.data?.address_text;
        const entity_name = data?.data?.name?.replace(/\./g, '');
        const abn = data?.data?.abn;
        const acn = data?.data?.acn;
        let entity_type_prefix = '';

        if (!type && entityTypeAbn?.toLowerCase()?.includes('company')) {
          type = EntityType.Company;
        }

        switch (type) {
          case EntityType.Trust:
            entity_type_prefix = 'trust_entity_details';
            break;
          case EntityType.Smsf:
            entity_type_prefix = 'smsf_entity_details';
            break;
          case EntityType.Company:
            entity_type_prefix = 'company_entity_details';
            break;
          case EntityType.Individual:
            entity_type_prefix = 'individual_entity_details';
            break;
          case EntityType.Association:
            entity_type_prefix = 'association_entity_details_id';
            break;
          case EntityType.Partnership:
            entity_type_prefix = 'partnership_entity_details';
            break;
        }

        await handleCreateEntity({
          // ...(apply_register_id ? { apply_register_id: Number(apply_register_id) } : {}),
          apply_register_id: Number(apply_register_id),
          type,
          state,
          postcode,
          send_information_about_other_investments: true,
          // address,
          // entity_name,
          country: (formik.values?.country ?? 'AUS') as CountryCodeEnum,
          // phone_number,
          individual_entity_details: {
            country: 'AUS',
          },
          [entity_type_prefix]: {
            country: (formik.values?.country ?? 'AUS') as CountryCodeEnum,
            abn,
            acn,
            type_original: trustType,
            type: ['bareTrust', 'hybridUnitTrust'].includes(String(trustType))
              ? 'otherType'
              : trustType,
            is_regulated: formik.values?.country !== 'AUS' ? undefined : isRegulated,
            registered_office: address,
            company_name: entity_name,
          },
        } as CreateEntityRequest);
      },
    });

  const { mutateAsync: createEntity, isLoading } = useCreateEntityMutation({
    onSuccess: data => {
      navigate(
        {
          pathname: entityPaths.static.updateEntity + `/${data.entity.id}`,
          search: params.toString(),
        },
        { state: { entityId: data.entity.id, country: formik?.values?.country } },
      );
    },
  });

  const { mutateAsync: updateEntity, isLoading: isLoadingUpdate } = useUpdateEntityMutation({
    onSuccess: data => {
      navigate(
        {
          pathname: entityPaths.static.updateEntity + `/${data.entity.id}`,
          search: params.toString(),
        },
        { state: { entityId: data.entity.id, country: formik?.values?.country } },
      );
      queryClient.invalidateQueries([USER_ENTITIES_QUERY_KEY]);
    },
    setErrors: formik.setErrors,
  });

  const handleCreateEntity = useCallback(
    async (payload: CreateEntityRequest) => {
      if (data?.entity?.entity_id) {
        await updateEntity({
          ...data?.entity, // ToDo: After changed type need delete all prev fields
          ...payload,
        });
      } else {
        await createEntity({
          ...payload,
          notifications_enabled: true,
          individual_entity_details: {
            country: 'AUS',
            ...(payload?.individual_entity_details ?? {}),
          },
          ...(apply_register_id ? { apply_register_id: Number(apply_register_id) } : {}),
        });
      }
    },
    [apply_register_id, createEntity, data?.entity, updateEntity],
  );

  const handleChangeSelect = (field: string, value: string | boolean | UseAbnOrAcn) => {
    formik.setFieldValue(field, value);
    formik.setFieldError(field, '');
    if (field === 'country' && value !== 'AUS') {
      handleChangeSelect('use_abn_acn', 'no');
    }
    if (field === 'country' && value === 'AUS') {
      handleChangeSelect('use_abn_acn', 'yes');
    }
    if (field === 'use_abn_acn' && value === 'no') {
      formik.setFieldValue('abn_acn', '');
      formik.setFieldError('abn_acn', '');
    }
  };

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const field = e.target.name;
    const value = onlyNumbers(e.target.value);
    formik.setFieldValue(field, value);
    formik.setFieldError(field, '');
  };

  const handleBack = useCallback(() => {
    navigate(
      { pathname: entityPaths.static.entityList, search: params.toString() },
      {
        replace: false,
      },
    );
  }, [navigate, params]);

  const mainAction = useMemo(
    () => ({
      name: 'Back to entities list',
      onClick: handleBack,
    }),
    [handleBack],
  );

  return (
    <SignUpPagesWrapper
      backAction={{ onClick: handleBack }}
      mainAction={mainAction}
      className="justify-start"
    >
      <FormikProvider value={formik}>
        <div className="max-w-[560px] w-full sm:h-full flex flex-col justify-center items-center px-6 gap-10">
          <p
            className="sm:text-[48px] sm:leading-[56px] text-[38px] leading-[46px] font-[600] font-[RecifeDisplay] overflow-hidden break-words"
            style={{ wordBreak: 'break-word' }}
          >
            Hey {currentUser?.first_name ?? ''}, in what country is your entity based?
          </p>

          <div className="flex flex-col gap-8 w-full">
            <Select
              isClearable
              label="Country"
              options={getSelectOptionByEnum<CountryNameCodeEnum>(CountryNameCodeEnum, true)}
              value={formik.values.country as unknown as CountryNameCodeEnum}
              onChange={value => handleChangeSelect('country', value || '')}
              error={formik.errors.country}
            />

            {formik.values.country === 'AUS' && (
              <>
                <div className="flex flex-col">
                  <span
                    className={classNames(
                      'text-gray-900 font-[Inter] text-[10px] leading-[18px] font-medium opacity-64 uppercase mb-2',
                    )}
                  >
                    Pull data automatically using your ABN or enter your full business details
                    manually?
                  </span>
                  <SelectButtons
                    options={getSelectOptionByEnum<UseAbnOrAcn>(UseAbnOrAcn, true, false)}
                    selected={getSelectOptionByEnum<UseAbnOrAcn>(UseAbnOrAcn).find(
                      ({ value }) => value === formik.values.use_abn_acn,
                    )}
                    disabled={!!data?.entity?.entity_id && !!data?.entity?.type}
                    capitalize={false}
                    onChange={value => {
                      handleChangeSelect('use_abn_acn', value);
                    }}
                  />
                </div>

                {formik.values.use_abn_acn === 'yes' && (
                  <TextInputField label={'ABN / ACN'} name="abn_acn" onChange={handleChangeInput} />
                )}
              </>
            )}
          </div>

          <div className="w-full flex flex-col items-center justify-between gap-4">
            <Button
              disabled={isLoading || isLoadingUpdate || isLoadingLookupByABN}
              loading={isLoading || isLoadingUpdate || isLoadingLookupByABN}
              onClick={formik.handleSubmit}
              className="w-full rounded-lg"
              type="submit"
            >
              Continue
            </Button>
          </div>
        </div>
      </FormikProvider>
    </SignUpPagesWrapper>
  );
};

export default withPrivateAccess(ABNLookupAndCountryPage, Role.SR_INVESTOR);
